import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listOrganizationPolicyExceptions,
  createOrganizationPolicyException,
  updateOrganizationPolicyException,
  deleteOrganizationPolicyException,
} from '@kirin/api-client/api/policy-exceptions';
import { ApiError } from '@kirin/api-client/errors';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import type {
  CreatePolicyException,
  UpdatePolicyException,
  PolicyExceptionAction,
} from '@kirin/api-client/contracts/policy-exceptions';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';
import { invalidatePackageListState } from './invalidatePackageListState';

/**
 * Hook to fetch allow/block rules for an organization policy
 *
 * Uses collaborative cache config since multiple admins can manage these lists.
 *
 * @param organizationId - The organization ID
 * @param policyKey - The policy key to fetch rules for
 * @param action - Optional filter by action type (allow/block)
 * @param enabled - Whether to enable the query
 */
export function useOrganizationPolicyExceptions(
  organizationId: OrganizationId | undefined,
  policyKey: string | undefined,
  action?: PolicyExceptionAction,
  enabled = true
) {
  const isEnabled = enabled && !!organizationId && !!policyKey;
  return useQuery({
    queryKey: queryKeys.policyExceptions.organization(
      organizationId ?? '',
      policyKey ?? '',
      action
    ),
    queryFn: async () => {
      if (!organizationId) throw new Error('organizationId is required');
      if (!policyKey) throw new Error('policyKey is required');
      const result = await listOrganizationPolicyExceptions(
        apiClient,
        organizationId,
        policyKey,
        action
      );
      return result.exceptions;
    },
    enabled: isEnabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Hook to create an allow/block rule for an organization
 *
 * @param organizationId - The organization ID
 * @param policyKey - The policy key to create the rule for
 */
export function useCreateOrganizationPolicyException(
  organizationId: OrganizationId | undefined,
  policyKey: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePolicyException) => {
      if (!organizationId) throw new Error('Organization ID is required');
      if (!policyKey) throw new Error('Policy key is required');
      return createOrganizationPolicyException(
        apiClient,
        organizationId,
        policyKey,
        data
      );
    },
    onSuccess: () => {
      invalidatePackageListState(queryClient, policyKey);
      if (organizationId && policyKey) {
        void queryClient.invalidateQueries({
          queryKey: [
            'policyExceptions',
            'organization',
            organizationId,
            policyKey,
          ],
        });
      }
    },
  });
}

/**
 * Hook to update an allow/block rule for an organization
 *
 * @param organizationId - The organization ID
 * @param policyKey - The policy key the rule belongs to
 */
export function useUpdateOrganizationPolicyException(
  organizationId: OrganizationId | undefined,
  policyKey: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      exceptionId,
      data,
    }: {
      exceptionId: string;
      data: UpdatePolicyException;
    }) => {
      if (!organizationId) throw new Error('Organization ID is required');
      if (!policyKey) throw new Error('Policy key is required');
      return updateOrganizationPolicyException(
        apiClient,
        organizationId,
        policyKey,
        exceptionId,
        data
      );
    },
    onSuccess: () => {
      invalidatePackageListState(queryClient, policyKey);
      if (organizationId && policyKey) {
        void queryClient.invalidateQueries({
          queryKey: [
            'policyExceptions',
            'organization',
            organizationId,
            policyKey,
          ],
        });
      }
    },
  });
}

/**
 * Hook to delete an allow/block rule for an organization
 *
 * @param organizationId - The organization ID
 * @param policyKey - The policy key the rule belongs to
 */
export function useDeleteOrganizationPolicyException(
  organizationId: OrganizationId | undefined,
  policyKey: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exceptionId: string) => {
      if (!organizationId) throw new Error('Organization ID is required');
      if (!policyKey) throw new Error('Policy key is required');
      try {
        return await deleteOrganizationPolicyException(
          apiClient,
          organizationId,
          policyKey,
          exceptionId
        );
      } catch (error) {
        // Row may already be gone (e.g. replaced by settings sync dual-write).
        if (error instanceof ApiError && error.status === 404) {
          return;
        }
        throw error;
      }
    },
    onSuccess: () => {
      invalidatePackageListState(queryClient, policyKey);
      if (organizationId && policyKey) {
        void queryClient.invalidateQueries({
          queryKey: [
            'policyExceptions',
            'organization',
            organizationId,
            policyKey,
          ],
        });
      }
    },
  });
}
